import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Zap, ArrowRight, Check, TrendingUp, MessageSquare, Bot, Target, BarChart3,
  ShieldCheck, Sparkles, Users, Building2, Store, Plane, GraduationCap,
  ShoppingCart, Briefcase, Network, Layers, Wand2, Activity, DollarSign,
} from "lucide-react";

const CUSTOMERS = [
  { icon: Building2, label: "SME" }, { icon: Plane, label: "Travel Agency" },
  { icon: Store, label: "Retail" }, { icon: ShoppingCart, label: "F&B" },
  { icon: Building2, label: "Property" }, { icon: GraduationCap, label: "Education" },
  { icon: Briefcase, label: "Professional Services" }, { icon: Network, label: "Franchise" },
  { icon: Users, label: "Cooperative" }, { icon: ShoppingCart, label: "E-Commerce" },
  { icon: Layers, label: "Marketing Agency" }, { icon: Building2, label: "Multi-Branch" },
];

const FEATURES = [
  { icon: Wand2, title: "AI Campaign Builder", desc: "Pick product, objective & budget. AI builds structure, audience, copy, creative briefs & tracking." },
  { icon: Bot, title: "AI Copywriter", desc: "Facebook, Instagram, Google & WhatsApp copy — multiple variants in Malay or English." },
  { icon: Sparkles, title: "AI Creative Engine", desc: "Concepts, banner variations, hooks, CTAs and video scripts across aspect ratios." },
  { icon: ShieldCheck, title: "AI Compliance Checker", desc: "Risk-check copy, creative, claims & tracking before publishing. Fix issues with AI." },
  { icon: Activity, title: "AI Campaign Doctor", desc: "Root-cause diagnosis across creative, audience, offer, tracking, leads & sales response." },
  { icon: MessageSquare, title: "WhatsApp Lead Centre", desc: "Ads → WhatsApp → AI qualification → staff routing → sale, with full attribution." },
  { icon: Target, title: "Smart Lead Routing & SLA", desc: "Route by branch, product, language & workload. Escalate overdue hot leads automatically." },
  { icon: TrendingUp, title: "Revenue Attribution & ROAS", desc: "Spend → lead → customer → staff → sale → payment → revenue. See real downstream results." },
  { icon: DollarSign, title: "Profit Optimization", desc: "Contribution ROAS after COGS, commission & ad spend. Favour real economics, not vanity revenue." },
  { icon: BarChart3, title: "Budget AI & Guardrails", desc: "Increase, maintain, reduce or pause with stop-loss & approval thresholds. Manual → Autopilot." },
];

const MODULES = [
  { id: "ai_booster", name: "AI Booster", price: 99 },
  { id: "creative_ai", name: "Creative AI", price: 99 },
  { id: "competitor_ai", name: "Competitor AI", price: 149 },
  { id: "market_ai", name: "Market AI", price: 99 },
  { id: "ads_manager_ai", name: "Ads Manager AI", price: 199 },
  { id: "optimization_ai", name: "Optimization AI", price: 149 },
  { id: "attribution_ai", name: "Attribution AI", price: 199 },
  { id: "landing_ai", name: "Landing AI", price: 79 },
  { id: "report_ai", name: "Report AI", price: 69 },
  { id: "agency_ai", name: "Agency AI", price: 299 },
];

function BuildYourOwn({ onStart }) {
  const [sel, setSel] = React.useState(["ai_booster", "creative_ai", "ads_manager_ai", "attribution_ai"]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const normal = MODULES.filter((m) => sel.includes(m.id)).reduce((a, m) => a + m.price, 0);
  const discount = sel.length >= 4 ? Math.round(normal * 0.15) : 0;
  const total = normal - discount;
  const rm = (n) => "RM" + n.toLocaleString("en-MY");

  return (
    <div className="mt-16 border-t border-border pt-16" id="build" data-testid="build-your-own">
      <div className="text-center mb-10">
        <span className="inline-block text-[10px] font-mono font-bold px-3 py-1 rounded-full bg-accent/15 text-accent mb-3">OR</span>
        <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Build Your Own AdsBoster</h3>
        <p className="mt-2 text-sm text-muted-foreground">Choose only what your business needs.</p>
      </div>
      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
          {MODULES.map((m) => {
            const on = sel.includes(m.id);
            return (
              <button key={m.id} data-testid={`module-${m.id}`} onClick={() => toggle(m.id)}
                className={`flex items-center justify-between p-4 rounded-xl border text-left transition-colors duration-200 ${on ? "border-primary/50 bg-primary/10" : "border-border bg-card hover:bg-secondary/40"}`}>
                <span className="flex items-center gap-3">
                  <span className={`w-5 h-5 rounded-md flex items-center justify-center border ${on ? "bg-primary border-primary" : "border-border"}`}>{on && <Check className="w-3.5 h-3.5 text-primary-foreground" />}</span>
                  <span className="text-sm font-medium">{m.name}</span>
                </span>
                <span className="font-mono text-sm text-muted-foreground">RM{m.price}</span>
              </button>
            );
          })}
        </div>
        <div className="bg-card border border-primary/40 rounded-2xl p-6 glow-amber lg:sticky lg:top-24">
          <p className="text-xs font-mono uppercase tracking-widest text-primary mb-4">Your AdsBoster</p>
          <div className="space-y-1.5 mb-4 min-h-[60px]">
            {sel.length === 0 && <p className="text-sm text-muted-foreground">Select modules to begin.</p>}
            {MODULES.filter((m) => sel.includes(m.id)).map((m) => (
              <div key={m.id} className="flex justify-between text-sm"><span className="text-muted-foreground">{m.name}</span><span className="font-mono">RM{m.price}</span></div>
            ))}
          </div>
          <div className="border-t border-border pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-muted-foreground"><span>Normal Price</span><span className="font-mono">{rm(normal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-accent"><span>Bundle Discount</span><span className="font-mono">-{rm(discount)}</span></div>}
            <div className="flex justify-between font-display font-bold text-lg pt-1"><span>Your Price</span><span data-testid="build-total">{rm(total)}<span className="text-xs text-muted-foreground">/mo</span></span></div>
          </div>
          <Button data-testid="build-start-btn" onClick={onStart} disabled={sel.length === 0} className="w-full mt-5 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl">Start 14-Day Trial</Button>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { t, lang, toggle } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [spend, setSpend] = useState([10000]);
  const [roas, setRoas] = useState([7]);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTrial = () => navigate(user ? "/dashboard" : "/login");

  const subscribe = async (tier) => {
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: tier.lookup_key,
        origin_url: window.location.origin,
        user_id: user?.user_id,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
    }
  };
  const rm = (n) => "RM " + n.toLocaleString("en-MY");
  const projRevenue = spend[0] * roas[0];
  const projProfit = Math.round(projRevenue * 0.4 - spend[0]);

  const ordinaryFlow = ["Impressions", "Clicks", "CTR", "CPC"];
  const usFlow = ["Ad Spend", "Lead", "Qualified", "Sale", "Payment", "Revenue", "Profit"];

  const tiers = [
    { name: "Starter", price: "RM99", lookup_key: "starter_monthly", tagline: "Get started with AI ads", features: ["AI Booster", "Creative AI (Basic)", "Ads Manager (Basic)", "Report AI (Basic)", "Lead Tracking"] },
    { name: "Growth", price: "RM299", lookup_key: "growth_monthly", tagline: "The core plan for growing SMEs", featured: true, badge: "MOST POPULAR", features: ["Everything in Starter +", "Full Creative AI", "Competitor AI", "Ads Manager AI", "Basic Attribution", "Reports"] },
    { name: "Pro", price: "RM599", lookup_key: "pro_monthly", tagline: "From ads management to revenue intelligence", badge: "REVENUE INTELLIGENCE", features: ["Everything in Growth +", "Market AI", "Optimization AI", "Full Attribution", "Landing AI", "Multi Account", "Advanced AI"] },
    { name: "Business", price: "RM999", lookup_key: "business_monthly", tagline: "Multi-brand / multi-branch teams", badge: "MULTI-BRAND", features: ["Everything in Pro +", "Multi-account & users", "Approval Workflow", "Advanced Attribution & Automation", "API / Integrations", "Management dashboard"] },
    { name: "Agency", price: "RM1,999", lookup_key: "agency_monthly", tagline: "Multi-client reseller channel", badge: "AGENCY", features: ["Up to 10 client workspaces", "White label options", "Branded client reports", "Competitor Intelligence per client", "Team & approvals", "Agency command centre"] },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground grid-noise overflow-x-hidden">
      {/* NAV */}
      <nav className={`fixed top-0 inset-x-0 z-50 transition-colors duration-200 ${scrolled ? "bg-background/85 backdrop-blur-xl border-b border-border" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center glow-amber">
              <Zap className="w-4.5 h-4.5 text-primary-foreground" fill="currentColor" />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight">AdsBoster<span className="text-primary"> AI</span></span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors" data-testid="nav-features">{t("nav.features")}</a>
            <a href="#difference" className="hover:text-foreground transition-colors" data-testid="nav-how">{t("nav.how")}</a>
            <a href="#pricing" className="hover:text-foreground transition-colors" data-testid="nav-pricing">{t("nav.pricing")}</a>
            <a href="#calculator" className="hover:text-foreground transition-colors" data-testid="nav-calculator">{t("nav.calculator")}</a>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="nav-lang-toggle" onClick={toggle} className="text-xs font-mono px-2.5 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors">
              {lang === "en" ? "BM" : "EN"}
            </button>
            <button data-testid="nav-login-button" onClick={() => navigate("/login")} className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:block">{t("nav.login")}</button>
            <Button data-testid="nav-trial-button" onClick={goTrial} className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl">{t("nav.trial")}</Button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="absolute top-[-5%] left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-primary/10 blur-[150px] pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-xs font-mono text-primary mb-8">
            <Zap className="w-3.5 h-3.5" /> {t("hero.badge")}
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]">
            {t("hero.title1")}<br />
            <span className="text-primary">{t("hero.title2")}</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.16 }}
            className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t("hero.sub")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.24 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button data-testid="hero-cta-trial-button" onClick={goTrial} className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base px-8 py-6 rounded-xl glow-amber">
              {t("hero.cta1")} <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
            <Button data-testid="hero-cta-demo-button" variant="outline" onClick={() => navigate("/login")} className="border-border bg-secondary hover:bg-secondary/70 text-foreground font-medium text-base px-8 py-6 rounded-xl">
              {t("hero.cta2")}
            </Button>
          </motion.div>
          <p className="mt-5 text-xs font-mono text-muted-foreground">{t("hero.from")}</p>

          <div className="mt-16 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs font-mono text-muted-foreground">
            {["Meta Ads", "Google Ads", "AI Creative", "AI Copywriting", "Leads", "WhatsApp", "CRM", "Attribution", "ROAS", "Revenue"].map((x) => (
              <span key={x} className="px-3 py-1 rounded-full bg-secondary/60 border border-border">{x}</span>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENCE */}
      <section id="difference" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-14">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("diff.title")}</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-card/60 border border-border rounded-2xl p-8">
            <p className="text-sm text-muted-foreground mb-6">{t("diff.ordinary")}</p>
            <div className="flex flex-wrap items-center gap-2">
              {ordinaryFlow.map((s, i) => (
                <React.Fragment key={s}>
                  <span className="px-3 py-2 rounded-lg bg-secondary text-muted-foreground text-sm font-mono">{s}</span>
                  {i < ordinaryFlow.length - 1 && <ArrowRight className="w-4 h-4 text-muted-foreground/40" />}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="bg-card border border-primary/30 rounded-2xl p-8 glow-amber">
            <p className="text-sm text-primary font-mono mb-6">{t("diff.us")}</p>
            <div className="flex flex-wrap items-center gap-2">
              {usFlow.map((s, i) => (
                <React.Fragment key={s}>
                  <span className={`px-3 py-2 rounded-lg text-sm font-mono font-semibold ${i >= 4 ? "bg-accent/15 text-accent" : "bg-primary/15 text-primary"}`}>{s}</span>
                  {i < usFlow.length - 1 && <ArrowRight className="w-4 h-4 text-primary/50" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        <p className="text-center mt-10 font-display text-lg sm:text-xl font-semibold text-accent">{t("diff.tagline")}</p>
      </section>

      {/* CUSTOMERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-14">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("customers.title")}</h2>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">{t("customers.sub")}</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {CUSTOMERS.map((c) => (
            <div key={c.label} className="flex flex-col items-center gap-3 p-5 rounded-xl bg-card/50 border border-border hover:border-primary/40 hover:-translate-y-1 transition-all duration-200" data-testid={`customer-${c.label.toLowerCase().replace(/\s|&/g, "-")}`}>
              <c.icon className="w-6 h-6 text-primary" />
              <span className="text-xs sm:text-sm text-center text-muted-foreground">{c.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-14">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("features.title")}</h2>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">{t("features.sub")}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
              className="group bg-card/60 border border-border rounded-2xl p-6 hover:border-primary/40 hover:-translate-y-1 transition-all duration-200" data-testid={`feature-card-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors duration-200">
                <f.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ROAS CALCULATOR */}
      <section id="calculator" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-card border border-border rounded-3xl p-8 sm:p-12">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t("calc.title")}</h2>
            <p className="mt-3 text-sm text-muted-foreground">{t("calc.sub")}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-8 mb-10">
            <div>
              <div className="flex justify-between text-sm mb-3"><span className="text-muted-foreground">{t("calc.spend")}</span><span className="font-mono text-primary">{rm(spend[0])}</span></div>
              <Slider data-testid="calc-spend-slider" value={spend} onValueChange={setSpend} min={1000} max={100000} step={1000} />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-3"><span className="text-muted-foreground">{t("calc.roas")}</span><span className="font-mono text-primary">{roas[0].toFixed(1)}x</span></div>
              <Slider data-testid="calc-roas-slider" value={roas} onValueChange={setRoas} min={1} max={15} step={0.5} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-primary/10 border border-primary/30 p-6 text-center">
              <p className="text-xs font-mono uppercase tracking-widest text-primary mb-2">{t("calc.revenue")}</p>
              <p className="font-display text-3xl font-extrabold" data-testid="calc-revenue-value">{rm(projRevenue)}</p>
            </div>
            <div className="rounded-2xl bg-accent/10 border border-accent/30 p-6 text-center">
              <p className="text-xs font-mono uppercase tracking-widest text-accent mb-2">{t("calc.profit")}</p>
              <p className="font-display text-3xl font-extrabold text-accent" data-testid="calc-profit-value">{rm(projProfit)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-14">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("pricing.title")}</h2>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">{t("pricing.sub")}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5 items-start">
          {tiers.map((tier) => (
            <div key={tier.name}
              className={`relative rounded-2xl p-6 border flex flex-col transition-all duration-200 ${tier.featured ? "bg-card border-primary/50 glow-amber lg:-translate-y-4 lg:scale-[1.03]" : "bg-card/50 border-border hover:border-primary/30"}`}
              data-testid={`pricing-card-${tier.name.toLowerCase().replace(/\s/g, "-")}`}>
              {tier.badge && (
                <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-mono font-bold px-3 py-1 rounded-full whitespace-nowrap ${tier.featured ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground border border-border"}`}>{tier.badge}</span>
              )}
              <h3 className="font-display text-lg font-bold mt-2">{tier.name}</h3>
              <p className="text-xs text-muted-foreground mt-1 h-8">{tier.tagline}</p>
              <div className="mt-3 mb-5">
                <span className="font-display text-3xl font-extrabold">{tier.price}</span>
                <span className="text-xs text-muted-foreground">{t("pricing.month")}</span>
              </div>
              <Button data-testid={`pricing-button-${tier.name.toLowerCase().replace(/\s/g, "-")}`} onClick={() => subscribe(tier)}
                className={`w-full rounded-xl font-semibold mb-6 ${tier.featured ? "bg-primary hover:bg-primary/90 text-primary-foreground" : "bg-secondary hover:bg-secondary/70 text-foreground"}`}>
                {tier.featured ? t("pricing.trial") : t("pricing.choose")}
              </Button>
              <ul className="space-y-2.5 text-xs text-muted-foreground">
                {tier.features.map((ft) => (
                  <li key={ft} className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-accent mt-0.5 shrink-0" /><span>{ft}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-center mt-10 text-sm text-muted-foreground font-mono">{t("pricing.annual")}</p>
        <BuildYourOwn onStart={goTrial} />
      </section>

      {/* BUNDLE TEASER */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl border border-accent/30 bg-accent/5 p-8 sm:p-12 text-center">
          <span className="inline-block text-[10px] font-mono font-bold px-3 py-1 rounded-full bg-accent/15 text-accent mb-4">COMING SOON</span>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t("bundle.title")}</h2>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">{t("bundle.sub")}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs font-mono">
            {["Ad", "WhatsApp", "Lead", "Salesperson", "Sale", "Revenue"].map((s, i, arr) => (
              <React.Fragment key={s}>
                <span className="px-3 py-1.5 rounded-lg bg-accent/10 text-accent border border-accent/20">{s}</span>
                {i < arr.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-accent/40" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">{t("final.title")}</h2>
        <p className="mt-6 text-xs sm:text-sm font-mono text-primary tracking-wide">{t("final.flow")}</p>
        <Button data-testid="final-cta-button" onClick={goTrial} className="mt-10 bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base px-10 py-6 rounded-xl glow-amber">
          {t("final.cta")} <ArrowRight className="w-5 h-5 ml-1" />
        </Button>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center"><Zap className="w-3.5 h-3.5 text-primary-foreground" fill="currentColor" /></div>
            <span className="font-display font-bold text-sm">AdsBoster AI</span>
          </div>
          <p className="text-xs text-muted-foreground">© 2026 AdsBoster AI · Turn Advertising Spend Into Measurable Business Growth.</p>
        </div>
      </footer>
    </div>
  );
}
